import {
    Card,
    CardContent,
    Divider,
    Fade,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { ChangeEvent, FunctionComponent, useState } from 'react';
import {
    ConditionLink,
    createPostImportConditionLinkDTOFromOriginal,
    PostImportConditionLinkDTO,
} from '@/models/conditionLink';
import { LoadingButton } from '@mui/lab';
import { LoadingContainer } from '@/components/Layout/Container';
import { useNavigate } from 'react-router-dom';
import { useAuthentication } from '@/hooks/useAuthentication';
import {
    useLazyGetConditionLinksImportQuery,
    usePostImportedConditionLinkMutation,
} from '@/api/v2/conditionLinks';
import { useNotification } from '@starjack/features/notifications';
import {
    ConditionLinkPersonTypeMapping,
    PersonTypeMap,
} from '@/pages/rulesets/components/ImportedCondtionLinkPersonTypeMapping';

export const NewImportConditionLinkCard: FunctionComponent = () => {
    const [baseData, setBaseData] = useState<{ name: string; id: '' }>({
        name: '',
        id: '',
    });
    const [conditionLinkImport, setConditionLinkImport] =
        useState<PostImportConditionLinkDTO>(null);
    const [conditionLinkImportOriginal, setConditionLinkImportOriginal] =
        useState<ConditionLink>(null);
    const [showMissingAsError, setShowMissingAsError] = useState(false);
    const navigate = useNavigate();
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { addNotification } = useNotification();
    const [getImport, { isFetching: getImportLoading }] =
        useLazyGetConditionLinksImportQuery();
    const [postImportConditionLink, { isLoading: postImportLoading }] =
        usePostImportedConditionLinkMutation();

    const validatePersonMapping = () => {
        if (!conditionLinkImport || !conditionLinkImportOriginal) {
            return true;
        }

        const missingMappedPersonTypes = Object.keys(
            conditionLinkImport.personTypesMapping
        ).filter((key) => !conditionLinkImport.personTypesMapping[key]);

        setShowMissingAsError(!!missingMappedPersonTypes.length);

        return !missingMappedPersonTypes.length;
    };

    const createImportConditionLink = () => {
        if (!validatePersonMapping()) {
            return;
        }

        postImportConditionLink({
            liftOperatorId,
            importConditionLink: conditionLinkImport,
        })
            .unwrap()
            .then((importConditionLink) => {
                addNotification({
                    message: 'Regelwerk erfolgreich importiert',
                    type: 'success',
                });

                navigate(
                    `/regelwerke/details?importId=${importConditionLink.id}`
                );
            })
            .catch((e) => {
                if (e.status === 400) {
                    const error = e.data.errors?.[0];

                    if (
                        error.error ===
                        'condition_link_must_belong_to_other_lift_operator'
                    ) {
                        addNotification({
                            message:
                                'Regelwerke können nur von einer anderen Bergbahn importiert werden',
                            type: 'error',
                        });

                        return;
                    }
                }

                addNotification({
                    message: 'Fehler beim Importieren des Regelwerks',
                    type: 'error',
                });
            });
    };

    const onConditionLinkSave = () => {
        getImport({
            importId: baseData.id,
        })
            .unwrap()
            .then((conditionLink) => {
                addNotification({
                    type: 'success',
                    message:
                        'Regelwerk wurde gefunden. Bitte alle Personen ihren Personentypen zuordnen',
                });

                setConditionLinkImportOriginal(conditionLink);
                setConditionLinkImport(
                    createPostImportConditionLinkDTOFromOriginal(conditionLink)
                );
            })
            .catch((e) => {
                if (e.status >= 400 && e.status < 500) {
                    addNotification({
                        type: 'error',
                        message:
                            'Fehler beim Importieren des Regelwerks. Bitte die ID überprüfen und noch einmal probieren',
                    });
                } else {
                    addNotification({
                        type: 'error',
                        message:
                            'Fehler beim Importieren des Regelwerks. Bitte später noch einmal probieren',
                    });
                }
            });
    };

    const updateConditionLink =
        (key: 'name' | 'id') => (e: ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;

            setBaseData((prev) => ({
                ...prev,
                [key]: value,
            }));
        };

    const updateImportConditionLinkPersonMap = (
        personTypeMap: PersonTypeMap
    ) => {
        setConditionLinkImport((prev) => ({
            ...prev,
            personTypesMapping: {
                ...personTypeMap,
            },
        }));
    };

    return (
        <Stack spacing={2}>
            <Typography
                sx={{
                    width: '75%',
                }}
            >
                Hier lassen sich allgemeine Daten für das Regelwerk festlegen.
                Bitte beachten: Um eine Kontingent- oder Preisregel zu
                erstellen, muss zuerst ein Name für das Regelwerk angegeben und
                gespeichert werden.
            </Typography>

            <Card
                sx={{
                    position: 'relative',
                }}
            >
                <CardContent
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant='h6'>Regelwerk importieren</Typography>

                    <LoadingButton
                        sx={{
                            ml: 'auto',
                        }}
                        onClick={createImportConditionLink}
                        variant='contained'
                        loading={postImportLoading}
                        disabled={!conditionLinkImportOriginal}
                    >
                        Regelwerk importieren
                    </LoadingButton>
                </CardContent>

                <Divider
                    sx={{
                        width: '100%',
                    }}
                />

                <CardContent>
                    <Stack
                        direction='row'
                        sx={{
                            mb: 6,
                            alignItems: 'center',
                        }}
                        spacing={3}
                    >
                        <TextField
                            sx={{
                                width: 400,
                            }}
                            value={baseData.id}
                            onChange={updateConditionLink('id')}
                            label='UUID'
                            disabled={!!baseData.name}
                        />

                        <LoadingButton
                            disabled={!baseData.id}
                            onClick={onConditionLinkSave}
                            variant='contained'
                            loading={getImportLoading}
                        >
                            Weiter
                        </LoadingButton>
                    </Stack>

                    {!!conditionLinkImport && (
                        <Stack
                            spacing={4}
                            sx={{
                                width: '75%',
                                mt: 8,
                            }}
                        >
                            <Typography variant='subtitle1'>
                                Einen Namen vergeben und gegebenenfalls die
                                fehlenden Personentypen zuordnen. Dann auf
                                &quot;Regelwerk importieren&quot; klicken, um
                                den Import abzuschließen.
                            </Typography>
                            <TextField
                                sx={{
                                    width: 400,
                                }}
                                value={conditionLinkImport.name}
                                onChange={(e) => {
                                    const { value } = e.target;

                                    setConditionLinkImport((prev) => ({
                                        ...prev,
                                        name: value,
                                    }));
                                }}
                                label='Name'
                            />

                            {!!Object.values(
                                conditionLinkImport.personTypesMapping
                            ).length && (
                                <ConditionLinkPersonTypeMapping
                                    onMappingChange={
                                        updateImportConditionLinkPersonMap
                                    }
                                    personTypes={
                                        conditionLinkImportOriginal.personTypes
                                    }
                                    personsToMap={
                                        conditionLinkImport.personTypesMapping
                                    }
                                    showMissingWithError={showMissingAsError}
                                />
                            )}
                        </Stack>
                    )}
                </CardContent>

                <Fade in={false}>
                    <div>
                        <LoadingContainer cover />
                    </div>
                </Fade>
            </Card>
        </Stack>
    );
};
